// Client/src/pages/digi/Invoice/TemplateDesigner/designModel.js
//
// Pure helpers for the invoice template design JSON. The shape is defined by
// Server/src/controllers/invoice/invoice-template/design.ts — the server
// normalises and validates whatever we send, so these helpers only need to keep
// the designer's own edits well-formed (ids unique, free elements framed, flow
// elements sized, rows only at the top of flow sections).
//
// Every function returns a NEW design; nothing mutates its input.

export const PX_PER_MM = 96 / 25.4;

// Mirrors design.ts (the server is the authority; these only drive the UI).
export const COLOR_TOKENS = ["primary", "accent", "ink", "muted", "line", "surface"];
export const TOKEN_LABELS = {
  primary: "Primary",
  accent: "Accent",
  ink: "Text",
  muted: "Muted text",
  line: "Lines",
  surface: "Surface",
};
export const FONT_FAMILIES = [
  "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins", "Raleway", "Nunito",
  "Oswald", "IBM Plex Sans", "Arimo", "Carlito", "Merriweather", "Playfair Display",
  "EB Garamond", "Gelasio", "PT Serif", "Tinos", "Courier Prime",
];
export const PAGE_SIZES = { letter: { w: 215.9, h: 279.4, label: "Letter (8.5 × 11 in)" }, a4: { w: 210, h: 297, label: "A4" } };
export const LIMITS = { bands: 12, elements: 120, tableColumns: 12, kvRows: 20, totalsRows: 6, rowColumns: 4 };

export const ELEMENT_LABELS = {
  text: "Text",
  image: "Image",
  shape: "Shape",
  keyValue: "Details list",
  propertiesList: "Properties list",
  lineTable: "Line items table",
  totals: "Totals",
  row: "Columns",
  divider: "Divider",
  spacer: "Spacer",
};
export const ELEMENT_ICONS = {
  text: "type",
  image: "image",
  shape: "square",
  keyValue: "list",
  propertiesList: "home",
  lineTable: "grid",
  totals: "hash",
  row: "columns",
  divider: "minus",
  spacer: "move",
};

const clone = (v) => JSON.parse(JSON.stringify(v));

export function newId(prefix = "el") {
  return `${prefix}${Math.random().toString(36).slice(2, 10)}`;
}

/** Page content box in mm (page minus margins). */
export function contentSize(page) {
  const base = PAGE_SIZES[page?.size] || PAGE_SIZES.letter;
  const [w, h] = page?.orientation === "landscape" ? [base.h, base.w] : [base.w, base.h];
  const m = page?.margins || { top: 12, right: 12, bottom: 12, left: 12 };
  return { w: Math.max(10, w - m.left - m.right), h: Math.max(10, h - m.top - m.bottom), pageW: w, pageH: h };
}

export function elementLabel(el) {
  if (!el) return "";
  if (el.name) return el.name;
  if (el.type === "text") {
    const first = (el.runs || []).map((r) => (r.text !== undefined ? r.text : `{${r.field}}`)).join("").trim();
    return first ? first.slice(0, 28).replace(/\n/g, " ") : "Text";
  }
  if (el.type === "image") return el.source === "vendorLogo" ? "Logo" : "Image";
  return ELEMENT_LABELS[el.type] || el.type;
}

// ---------------------------------------------------------------- factories

const t = (text, marks = {}) => ({ text, ...marks });
const f = (field, format, marks = {}) => ({ field, ...(format ? { format } : {}), ...marks });

/**
 * A new element of `type`. `inFree` decides whether it gets a frame (free
 * section) or flow sizing. `opts.at` = { x, y } in mm for free placement.
 */
export function makeElement(type, inFree, opts = {}) {
  const id = newId("el");
  const sizes = {
    text: [60, 8], image: [45, 28], shape: [40, 20], keyValue: [80, 18], propertiesList: [80, 8],
    lineTable: [180, 40], totals: [70, 18], divider: [100, 2], spacer: [40, 6], row: [100, 20],
  };
  const [w, h] = sizes[type] || [40, 10];
  const place = inFree
    ? { frame: { x: Math.max(0, round1(opts.at?.x ?? 6)), y: Math.max(0, round1(opts.at?.y ?? 4)), w, h } }
    : { widthPct: 100, marginTop: 2, blockAlign: "left" };
  let body;
  switch (type) {
    case "text":
      body = { runs: opts.runs || [t("Double-click to edit text")] };
      break;
    case "image":
      body = opts.assetId ? { source: "asset", assetId: opts.assetId, fit: "contain" } : { source: "vendorLogo", fit: "contain" };
      if (!inFree) place.widthPct = 35;
      break;
    case "shape":
      body = { shape: opts.shape || "rect", fill: "accent", strokeWidth: 0 };
      if (opts.shape === "line") {
        body = { shape: "line", stroke: "line", strokeWidth: 1 };
        if (inFree) place.frame.h = 2;
      }
      break;
    case "keyValue":
      body = {
        rows: [
          { id: newId("kv"), label: [t("Invoice #:", { bold: true })], value: [f("invoice.number")] },
          { id: newId("kv"), label: [t("Date:", { bold: true })], value: [f("invoice.date", "MMMM d, yyyy")] },
        ],
        labelWidthPct: 45,
        rowGap: 1,
      };
      if (!inFree) place.widthPct = 50;
      break;
    case "propertiesList":
      body = { separator: "comma", withUnits: false };
      break;
    case "lineTable":
      body = {
        columns: [
          { id: newId("col"), label: "Date of service", field: "line.date", widthPct: 17, align: "center", format: "MMMM" },
          { id: newId("col"), label: "Description", field: "line.descriptionWithComment", widthPct: 37, align: "left" },
          { id: newId("col"), label: "Qty.", field: "line.qty", widthPct: 10, align: "center" },
          { id: newId("col"), label: "Amount", field: "line.rate", widthPct: 18, align: "right" },
          { id: newId("col"), label: "Total", field: "line.subtotal", widthPct: 18, align: "right" },
        ],
        headerStyle: { fontWeight: 700, background: "accent" },
        borders: "grid",
        fillerRows: 0,
        groupByBuilding: false,
        splitCurrency: false,
      };
      break;
    case "totals":
      body = {
        rows: [
          { id: newId("tr"), key: "subtotal", label: [t("Subtotal")] },
          { id: newId("tr"), key: "gst", label: [t("GST ("), f("totals.gstRate"), t(")")] },
          { id: newId("tr"), key: "total", label: [t("Total", { bold: true })], style: { fontWeight: 700 } },
        ],
        borders: "none",
        splitCurrency: false,
        labelWidthPct: 55,
      };
      if (!inFree) Object.assign(place, { widthPct: 40, blockAlign: "right" });
      break;
    case "row":
      body = {
        gap: 6,
        columns: [
          { id: newId("rc"), widthPct: 50, elements: [] },
          { id: newId("rc"), widthPct: 50, elements: [] },
        ],
      };
      break;
    case "divider":
      body = { thickness: 1, color: "line" };
      break;
    case "spacer":
      body = { height: 6 };
      break;
    default:
      body = {};
  }
  return { id, type, ...body, ...place, ...(opts.style ? { style: opts.style } : {}) };
}

export function makeBand(kind, label) {
  return {
    id: newId("band"),
    label: label || (kind === "free" ? "Free section" : "Flowing section"),
    kind,
    height: 40,
    pin: "none",
    elements: [],
  };
}

const round1 = (n) => Math.round(Number(n) * 10) / 10;

// ---------------------------------------------------------------- lookup

/**
 * Where an element lives: { el, band, bandIndex, col (row column or null),
 * row (the row element or null), list (the array holding it), index }.
 */
export function locate(design, id) {
  for (let bi = 0; bi < (design?.bands || []).length; bi++) {
    const band = design.bands[bi];
    for (let i = 0; i < band.elements.length; i++) {
      const el = band.elements[i];
      if (el.id === id) return { el, band, bandIndex: bi, col: null, row: null, list: band.elements, index: i };
      if (el.type === "row") {
        for (const col of el.columns) {
          const ci = col.elements.findIndex((e) => e.id === id);
          if (ci >= 0) return { el: col.elements[ci], band, bandIndex: bi, col, row: el, list: col.elements, index: ci };
        }
      }
    }
  }
  return null;
}

export const findBand = (design, bandId) => (design?.bands || []).find((b) => b.id === bandId) || null;

/** Every element with its band, depth-first (rows before their children). */
export function allElements(design) {
  const out = [];
  for (const band of design?.bands || []) {
    for (const el of band.elements) {
      out.push({ el, band, col: null });
      if (el.type === "row") el.columns.forEach((col) => col.elements.forEach((c) => out.push({ el: c, band, col })));
    }
  }
  return out;
}

export const countElements = (design) => allElements(design).length;

/** The row column with this id, plus its row and band. */
export function locateColumn(design, colId) {
  for (const band of design?.bands || []) {
    for (const el of band.elements) {
      if (el.type !== "row") continue;
      const col = el.columns.find((c) => c.id === colId);
      if (col) return { col, row: el, band };
    }
  }
  return null;
}

// ---------------------------------------------------------------- edits

/** Apply `fn(el)` (returns the new element) or merge an object patch. */
export function updateElement(design, id, patch) {
  const d = clone(design);
  const loc = locate(d, id);
  if (!loc) return design;
  loc.list[loc.index] = typeof patch === "function" ? patch(clone(loc.el)) : { ...loc.el, ...patch };
  return d;
}

export function updateBand(design, bandId, patch) {
  const d = clone(design);
  const i = d.bands.findIndex((b) => b.id === bandId);
  if (i < 0) return design;
  d.bands[i] = typeof patch === "function" ? patch(clone(d.bands[i])) : { ...d.bands[i], ...patch };
  return d;
}

export function updatePage(design, patch) {
  const d = clone(design);
  d.page = typeof patch === "function" ? patch(clone(d.page)) : { ...d.page, ...patch };
  return d;
}

export function removeElement(design, id) {
  const d = clone(design);
  const loc = locate(d, id);
  if (!loc) return design;
  loc.list.splice(loc.index, 1);
  return d;
}

// Fit an element to the container kind it is moving into.
function adaptPlacement(el, toFree, frame) {
  const out = { ...el };
  if (toFree) {
    delete out.widthPct;
    delete out.marginTop;
    delete out.blockAlign;
    out.frame = frame || el.frame || { x: 6, y: 4, w: 60, h: 10 };
  } else {
    delete out.frame;
    out.widthPct = el.widthPct ?? 100;
    out.marginTop = el.marginTop ?? 2;
    out.blockAlign = el.blockAlign || "left";
  }
  return out;
}

/** Can an element of this type go into that target? Returns a reason or null. */
export function placementError(design, type, target) {
  const band = findBand(design, target.bandId);
  if (!band) return "Pick a section first";
  if (type === "row" && (band.kind === "free" || target.colId)) return "Columns can only go directly into a flowing section";
  return null;
}

/**
 * Insert `el` at target { bandId, colId?, index?, frame? }. Index defaults to the
 * end. Returns the design unchanged when the placement isn't allowed.
 */
export function insertElement(design, el, target) {
  if (placementError(design, el.type, target)) return design;
  const d = clone(design);
  const band = findBand(d, target.bandId);
  const col = target.colId ? locateColumn(d, target.colId)?.col : null;
  const list = col ? col.elements : band.elements;
  const placed = adaptPlacement(el, band.kind === "free" && !col, target.frame);
  const idx = target.index === undefined || target.index < 0 || target.index > list.length ? list.length : target.index;
  list.splice(idx, 0, placed);
  return d;
}

/** Move an element to another place (same or different section/column). */
export function moveElement(design, id, target) {
  const loc = locate(design, id);
  if (!loc) return design;
  if (placementError(design, loc.el.type, target)) return design;
  if (loc.el.type === "row" && target.colId) return design;
  // Moving within the same list: account for the removal shifting indexes.
  const sameList = loc.band.id === target.bandId && (loc.col?.id || null) === (target.colId || null);
  let index = target.index;
  if (sameList && index !== undefined && index > loc.index) index -= 1;
  const without = removeElement(design, id);
  return insertElement(without, loc.el, { ...target, index });
}

/** Swap an element with its neighbour in the same list (dir -1 up, +1 down). */
export function shiftElement(design, id, dir) {
  const loc = locate(design, id);
  if (!loc) return design;
  const j = loc.index + dir;
  if (j < 0 || j >= loc.list.length) return design;
  const d = clone(design);
  const l2 = locate(d, id);
  const [item] = l2.list.splice(l2.index, 1);
  l2.list.splice(j, 0, item);
  return d;
}

// New ids everywhere inside a copied element (rows, columns, kv/totals rows).
function reId(el) {
  const out = clone(el);
  out.id = newId("el");
  if (out.type === "row") out.columns = out.columns.map((c) => ({ ...c, id: newId("rc"), elements: c.elements.map(reId) }));
  if (out.type === "keyValue") out.rows = out.rows.map((r) => ({ ...r, id: newId("kv") }));
  if (out.type === "totals") out.rows = out.rows.map((r) => ({ ...r, id: newId("tr") }));
  if (out.type === "lineTable") out.columns = out.columns.map((c) => ({ ...c, id: newId("col") }));
  return out;
}

/** Duplicate next to the original; free copies are offset 4 mm. Returns { design, id }. */
export function duplicateElement(design, id) {
  const loc = locate(design, id);
  if (!loc) return { design, id: null };
  const copy = reId(loc.el);
  if (copy.frame) copy.frame = { ...copy.frame, x: copy.frame.x + 4, y: copy.frame.y + 4 };
  delete copy.locked;
  const d = clone(design);
  const l2 = locate(d, id);
  l2.list.splice(l2.index + 1, 0, copy);
  return { design: d, id: copy.id };
}

export function addBand(design, kind, index) {
  const d = clone(design);
  const band = makeBand(kind);
  const at = index === undefined ? d.bands.length : Math.max(0, Math.min(index, d.bands.length));
  d.bands.splice(at, 0, band);
  return { design: d, id: band.id };
}

export function removeBand(design, bandId) {
  const d = clone(design);
  d.bands = d.bands.filter((b) => b.id !== bandId);
  return d;
}

export function shiftBand(design, bandId, dir) {
  const d = clone(design);
  const i = d.bands.findIndex((b) => b.id === bandId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= d.bands.length) return design;
  const [b] = d.bands.splice(i, 1);
  d.bands.splice(j, 0, b);
  return d;
}

export function duplicateBand(design, bandId) {
  const d = clone(design);
  const i = d.bands.findIndex((b) => b.id === bandId);
  if (i < 0) return { design, id: null };
  const copy = { ...clone(d.bands[i]), id: newId("band"), label: `${d.bands[i].label} (copy)`.slice(0, 40) };
  copy.elements = copy.elements.map(reId);
  d.bands.splice(i + 1, 0, copy);
  return { design: d, id: copy.id };
}

/**
 * Switch a section between free and flowing. Free → flow keeps element order
 * (top to bottom, then left to right) and drops positions; flow → free stacks
 * the elements down the section and grows it to fit. Rows can't live in a free
 * section, so their children are lifted out in order.
 */
export function convertBand(design, bandId, kind, contentWidthMm) {
  return updateBand(design, bandId, (band) => {
    if (band.kind === kind) return band;
    if (kind === "flow") {
      const ordered = [...band.elements].sort(
        (a, b) => (a.frame?.y ?? 0) - (b.frame?.y ?? 0) || (a.frame?.x ?? 0) - (b.frame?.x ?? 0)
      );
      return {
        ...band,
        kind,
        pin: "none",
        elements: ordered.map((el) => {
          const out = adaptPlacement(el, false);
          if (el.frame && contentWidthMm) out.widthPct = Math.max(5, Math.min(100, Math.round((el.frame.w / contentWidthMm) * 100)));
          return out;
        }),
      };
    }
    let y = 2;
    const flat = [];
    for (const el of band.elements) {
      if (el.type === "row") el.columns.forEach((c) => c.elements.forEach((x) => flat.push(x)));
      else flat.push(el);
    }
    const elements = flat.map((el) => {
      const w = contentWidthMm ? Math.max(10, ((el.widthPct ?? 100) / 100) * contentWidthMm) : 60;
      const h = el.type === "lineTable" ? 40 : el.type === "keyValue" || el.type === "totals" ? 18 : 10;
      const frame = { x: 0, y, w: round1(w), h };
      y += h + 2;
      return adaptPlacement(el, true, frame);
    });
    return { ...band, kind, height: Math.max(band.height || 40, Math.min(250, round1(y + 2))), elements };
  });
}

/** Frames of every free element in a band, except one (for snapping). */
export function siblingFrames(band, exceptId) {
  if (!band || band.kind !== "free") return [];
  return band.elements.filter((e) => e.id !== exceptId && !e.hidden && e.frame).map((e) => e.frame);
}

// ---------------------------------------------------------------- runs <-> text

/**
 * Text runs as the editable string vendors type: fields appear as {{Label}}.
 * `labels` maps field key → label.
 */
export function runsToText(runs, labels) {
  return (runs || []).map((r) => (r.field !== undefined ? `{{${labels[r.field] || r.field}}}` : r.text || "")).join("");
}

/**
 * Parse the edited string back into runs. Tokens matching a field label or key
 * become field runs; anything else stays text. Text is split after each newline
 * so every line can be styled on its own. Marks (bold, colour, format) are kept
 * from the previous runs where the segment is unchanged in kind and position.
 */
export function textToRuns(text, labels, previous = []) {
  const byLabel = {};
  Object.entries(labels).forEach(([k, l]) => {
    byLabel[l.toLowerCase()] = k;
    byLabel[k.toLowerCase()] = k;
  });
  const segments = [];
  const re = /\{\{([^{}]+)\}\}/g;
  let last = 0;
  let m;
  const pushText = (s) => {
    if (!s) return;
    // Split after each newline (no regex lookbehind: older Safari lacks it).
    let start = 0;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === "\n") {
        segments.push({ text: s.slice(start, i + 1) });
        start = i + 1;
      }
    }
    if (start < s.length) segments.push({ text: s.slice(start) });
  };
  while ((m = re.exec(text))) {
    const key = byLabel[m[1].trim().toLowerCase()];
    if (!key) continue;
    pushText(text.slice(last, m.index));
    segments.push({ field: key });
    last = m.index + m[0].length;
  }
  pushText(text.slice(last));

  // Carry marks over: the nth text segment inherits from the nth previous text
  // run, the nth field from the nth previous run of the same field.
  const prevText = previous.filter((r) => r.field === undefined);
  const prevFields = previous.filter((r) => r.field !== undefined);
  let ti = 0;
  const usedField = {};
  return segments.map((s) => {
    const marks = (r) => {
      if (!r) return {};
      const out = {};
      ["bold", "italic", "underline", "color"].forEach((k) => r[k] !== undefined && (out[k] = r[k]));
      return out;
    };
    if (s.field) {
      const n = (usedField[s.field] = (usedField[s.field] || 0) + 1);
      const prev = prevFields.filter((r) => r.field === s.field)[n - 1];
      return { field: s.field, ...(prev?.format ? { format: prev.format } : {}), ...marks(prev) };
    }
    return { text: s.text, ...marks(prevText[ti++]) };
  });
}

export const fieldLabelMap = (fieldGroups) => {
  const out = {};
  (fieldGroups || []).forEach((g) => g.fields.forEach((fd) => (out[fd.key] = fd.label)));
  return out;
};

export const fieldKindMap = (fieldGroups) => {
  const out = {};
  (fieldGroups || []).forEach((g) => g.fields.forEach((fd) => (out[fd.key] = fd.kind)));
  return out;
};

/** Document-level fields only (line.* fields belong to the table). */
export const docFieldGroups = (fieldGroups) => (fieldGroups || []).filter((g) => !g.fields.some((fd) => fd.key.startsWith("line.")));
export const lineFields = (fieldGroups) =>
  (fieldGroups || []).flatMap((g) => g.fields).filter((fd) => fd.key.startsWith("line."));
